import type React from "react";
import { useEffect, useRef } from "react";
import { clsx } from "clsx";
import { cva } from "class-variance-authority";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { TypingDots } from "@ui/components/core/BaseChat";
import type { OrbMessage, AgentMessageStyle, UserMessageStyle } from "./types";
import { useMessageLayout } from "./useMessageLayout";

const TYPING_MESSAGE_ID = "typing";

export function TextMessageList({
  messages: rawMessages,
  alignMessages = "bottom",
  fillHeight = false,
  agentMessageStyle = "default",
  userMessageStyle = "default",
  gap = "default",
  pointyCorners = false,
  surface = "eggshell",
  showTypingBubble = false,
  disableInternalScroll = false,
  contentClassName,
}: {
  messages: OrbMessage[];
  alignMessages?: "top" | "bottom";
  fillHeight?: boolean;
  agentMessageStyle?: AgentMessageStyle;
  userMessageStyle?: UserMessageStyle;
  /** 4px gap between same-role messages, 12px between different roles */
  gap?: "default" | "compact";
  /** Asymmetric border-radius: one corner at 8px */
  pointyCorners?: boolean;
  surface?: "eggshell" | "cream";
  showTypingBubble?: boolean;
  disableInternalScroll?: boolean;
  contentClassName?: string;
}) {
  const { messages, layoutKey, messagesInitial } = useMessageLayout(rawMessages);
  const displayMessages = showTypingBubble
    ? [...messages, { id: TYPING_MESSAGE_ID, role: "agent" as const, message: "" }]
    : messages;
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fillHeight || disableInternalScroll || !scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [disableInternalScroll, fillHeight, displayMessages]);

  return (
    <div
      ref={scrollRef}
      className={clsx(
        "tw-flex tw-flex-col",
        fillHeight
          ? disableInternalScroll
            ? "tw-min-h-full tw-overflow-visible"
            : "tw-h-full tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-scrollbar-none"
          : "tw-absolute tw-inset-x-0 tw-top-0 -tw-bottom-8 tw-pt-16 tw-pb-8",
      )}
      style={
        fillHeight
          ? undefined
          : { mask: "linear-gradient(to bottom, transparent, white 4rem, white calc(100% - 2rem), transparent)" }
      }
    >
      <div
        className={clsx(
          disableInternalScroll ? "tw-min-h-full" : "tw-min-h-full tw-flex tw-items-end",
          alignMessages === "bottom" && "tw-mt-auto",
          contentClassName,
        )}
      >
        <m.div
          key={layoutKey}
          layout={messages.length > 0 ? "position" : undefined}
          className={clsx("tw-flex-auto tw-flex tw-flex-col", gap === "default" && "tw-gap-4")}
        >
          <AnimatePresence mode='popLayout' initial={messagesInitial}>
            {displayMessages.map((message, index) => (
              <TextMessage
                key={message.id}
                {...message}
                agentMessageStyle={agentMessageStyle}
                userMessageStyle={userMessageStyle}
                pointyCorners={pointyCorners}
                surface={surface}
                marginTop={
                  gap === "compact" && index > 0
                    ? displayMessages[index - 1].role === message.role
                      ? "0.25rem"
                      : "0.75rem"
                    : undefined
                }
                isTyping={message.id === TYPING_MESSAGE_ID}
              />
            ))}
          </AnimatePresence>
        </m.div>
      </div>
    </div>
  );
}

const BUBBLE_CLASSES = "tw-w-fit tw-py-2.5 tw-px-4 tw-max-w-[calc(100%-4rem)]";

const POINTY_RADIUS = { agent: "10px 10px 10px 4px", user: "10px 10px 4px 10px" } as const;

const textMessageVariants = cva("tw-text-sm/5 tw-text-black", {
  variants: {
    role: {
      user: `tw-ml-auto ${BUBBLE_CLASSES}`,
      agent: "",
    },
    agentStyle: {
      default: "",
      dark: "",
      warm: "",
    },
    userStyle: {
      default: "",
      elevated: "",
    },
    pointyCorners: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    { role: "agent", agentStyle: "dark", className: `${BUBBLE_CLASSES} tw-bg-gray-200` },
    { role: "agent", agentStyle: "warm", className: BUBBLE_CLASSES },
    { role: "agent", agentStyle: "dark", pointyCorners: false, className: "tw-rounded-2.5xl" },
    { role: "agent", agentStyle: "warm", pointyCorners: false, className: "tw-rounded-2.5xl" },
    { role: "user", pointyCorners: false, className: "tw-rounded-2.5xl" },
    { role: "user", userStyle: "default", className: "tw-bg-gray-200" },
    { role: "user", userStyle: "elevated", className: "tw-bg-white tw-mt-1 tw-mr-1" },
  ],
});

const USER_ELEVATED_STYLE: React.CSSProperties = {
  boxShadow: "0 0 0 0.897px rgba(0, 0, 0, 0.07), 0 2px 4px 0 rgba(0, 0, 0, 0.08)",
};

function TextMessage({
  message,
  role,
  className,
  agentMessageStyle = "default",
  userMessageStyle = "default",
  pointyCorners = false,
  marginTop,
  surface = "eggshell",
  isTyping = false,
}: OrbMessage & {
  className?: string;
  agentMessageStyle?: AgentMessageStyle;
  userMessageStyle?: UserMessageStyle;
  pointyCorners?: boolean;
  marginTop?: number | string;
  surface?: "eggshell" | "cream";
  isTyping?: boolean;
}) {
  const isBubble = role === "user" || (role === "agent" && agentMessageStyle !== "default");

  const style: React.CSSProperties = {};
  if (role === "user" && userMessageStyle === "elevated") Object.assign(style, USER_ELEVATED_STYLE);
  if (role === "agent" && agentMessageStyle === "warm") {
    if (surface === "eggshell") {
      style.backgroundColor = "#F5F3F1";
    } else if (surface === "cream") {
      style.backgroundColor = "#EBE8E4";
    }
  }
  if (pointyCorners && isBubble) style.borderRadius = POINTY_RADIUS[role as "agent" | "user"];
  if (marginTop !== undefined) style.marginTop = marginTop;

  return (
    <m.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={textMessageVariants({
        role,
        agentStyle: agentMessageStyle,
        userStyle: userMessageStyle,
        pointyCorners,
        className,
      })}
      style={Object.keys(style).length > 0 ? style : undefined}
    >
      {isTyping ? <TypingDots /> : message}
    </m.p>
  );
}
